// src/pages/admin/AdminApiKeysPage.jsx
import React, { useEffect, useState } from "react";
import { api } from "../scripts/api"; // your axios instance
import { success, error } from "../utils/toastHelpers";

export default function AdminApiKeysPage() {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [form, setForm] = useState({
    name: "",
    permissions: "inventory.create,inventory.read", // default
    expires_at: "", // ISO date string or empty
  });
  const [creating, setCreating] = useState(false);
  const [newKey, setNewKey] = useState(null); // to show just-created key once

  // Load all keys on mount
  useEffect(() => {
    loadKeys();
  }, []);

  async function loadKeys() {
  setLoading(true);
  setErr("");
  try {
    const res = await api.get("/api-keys");
    const apiData = res.data;

    if (Array.isArray(apiData)) {
      setKeys(apiData);
    } else if (apiData?.ok && Array.isArray(apiData.keys)) {
      setKeys(apiData.keys);
    } else if (apiData?.keys) {
      setKeys(apiData.keys);
    } else {
      setKeys([]);
      console.warn("Unexpected API keys response shape:", apiData);
    }
  } catch (e) {
    setErr(e?.response?.data?.error || "Failed to load API keys");
    console.error(e);
  } finally {
    setLoading(false);
  }
}

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      error("Name is required");
      return;
    }

    setCreating(true);
    setNewKey(null);
    setErr("");

    try {
      const payload = {
        name: form.name.trim(),
        permissions: form.permissions.trim().split(",").map(p => p.trim()).filter(Boolean),
        expires_at: form.expires_at || null,
      };

      const res = await api.post("/api-keys", payload);

      if (res.data?.ok && res.data?.key) {
        success(`API key created: ${res.data.key.slice(0, 10)}...`);
        setNewKey(res.data.key); // show full key once
        setKeys((prev) => [...prev, {
          id: res.data.id || Date.now(),
          key: res.data.key,
          name: form.name.trim(),
          permissions: payload.permissions,
          created_at: new Date().toISOString(),
          active: 1,
        }]);
        setForm({ name: "", permissions: "inventory.create,inventory.read", expires_at: "" });
      }
    } catch (e) {
      const msg = e?.response?.data?.error || "Failed to create API key";
      error(msg);
      setErr(msg);
    } finally {
      setCreating(false);
    }
  };

  const toggleActive = async (id, currentActive) => {
    if (!window.confirm(`Really ${currentActive ? "deactivate" : "activate"} this key?`)) return;

    try {
      await api.patch(`/api-keys/${id}`, { active: currentActive ? 0 : 1 });
      success("API key status updated");
      setKeys((prev) =>
        prev.map((k) => (k.id === id ? { ...k, active: currentActive ? 0 : 1 } : k))
      );
    } catch (e) {
      error("Failed to update key status");
    }
  };

  const revokeKey = async (id) => {
    if (!window.confirm("Revoke (delete) this API key? This cannot be undone.")) return;

    try {
      await api.delete(`/api-keys/${id}`);
      success("API key revoked");
      setKeys((prev) => prev.filter((k) => k.id !== id));
    } catch (e) {
      error("Failed to revoke key");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    success("API key copied to clipboard");
  };

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: "0 auto" }}>
      <h1 style={{ fontSize: 28, fontWeight: 800, marginBottom: 8 }}>Manage API Keys</h1>
      <p className="muted" style={{ marginBottom: 24 }}>
        Create and manage API keys for integrations (e.g. quoting app → IMS inventory).
      </p>

      {err && (
        <div className="card" style={{ padding: 16, marginBottom: 24, border: "1px solid #ff4d4f" }}>
          <div style={{ color: "#ff4d4f", fontWeight: 700 }}>Error</div>
          <div>{err}</div>
        </div>
      )}

      {/* Create Form */}
      <div className="card" style={{ padding: 20, marginBottom: 32 }}>
        <h2 style={{ fontSize: 20, marginBottom: 16 }}>Create New API Key</h2>

        <form onSubmit={handleSubmit} style={{ display: "grid", gap: 16 }}>
          <div>
            <label className="muted" style={{ display: "block", marginBottom: 6, fontSize: 13 }}>
              Key Name (e.g. "Quoting App Integration")
            </label>
            <input
              className="input"
              name="name"
              value={form.name}
              onChange={handleChange}
              placeholder="e.g. Quoting App Sync"
              required
              disabled={creating}
            />
          </div>

          <div>
            <label className="muted" style={{ display: "block", marginBottom: 6, fontSize: 13 }}>
              Permissions (comma-separated)
            </label>
            <input
              className="input"
              name="permissions"
              value={form.permissions}
              onChange={handleChange}
              placeholder="inventory.create,inventory.read,inventory.update"
              disabled={creating}
            />
          </div>

          <div>
            <label className="muted" style={{ display: "block", marginBottom: 6, fontSize: 13 }}>
              Expires At (optional ISO date, e.g. 2026-12-31T23:59:59Z)
            </label>
            <input
              className="input"
              name="expires_at"
              type="datetime-local"
              value={form.expires_at ? form.expires_at.slice(0, 16) : ""}
              onChange={(e) => setForm(p => ({ ...p, expires_at: e.target.value ? `${e.target.value}:00Z` : "" }))}
              disabled={creating}
            />
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <button className="btn btn-success" type="submit" disabled={creating || !form.name.trim()}>
              {creating ? "Creating..." : "Generate API Key"}
            </button>
          </div>
        </form>

        {newKey && (
          <div style={{ marginTop: 24, padding: 16, background: "rgba(34,197,94,0.15)", borderRadius: 12, border: "1px solid rgba(34,197,94,0.3)" }}>
            <div style={{ fontWeight: 700, marginBottom: 8 }}>New API Key (copy now — shown only once)</div>
            <div style={{ display: "flex", alignItems: "center", gap: 12, wordBreak: "break-all" }}>
              <code style={{ fontSize: 15, flex: 1 }}>{newKey}</code>
              <button
                className="btn-outline"
                onClick={() => copyToClipboard(newKey)}
                style={{ whiteSpace: "nowrap" }}
              >
                Copy
              </button>
            </div>
            <div className="muted" style={{ fontSize: 12, marginTop: 8 }}>
              Store this securely — it will not be shown again.
            </div>
          </div>
        )}
      </div>

      {/* Existing Keys Table */}
      <div className="card" style={{ padding: 20 }}>
        <h2 style={{ fontSize: 20, marginBottom: 16 }}>Existing API Keys</h2>

        {loading ? (
          <div className="muted">Loading keys...</div>
        ) : keys.length === 0 ? (
          <div className="muted">No API keys created yet.</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
                  <th style={{ padding: "12px 8px", textAlign: "left" }}>Name</th>
                  <th style={{ padding: "12px 8px", textAlign: "left" }}>Key (partial)</th>
                  <th style={{ padding: "12px 8px", textAlign: "left" }}>Permissions</th>
                  <th style={{ padding: "12px 8px", textAlign: "left" }}>Created</th>
                  <th style={{ padding: "12px 8px", textAlign: "left" }}>Expires</th>
                  <th style={{ padding: "12px 8px", textAlign: "center" }}>Active</th>
                  <th style={{ padding: "12px 8px", textAlign: "center" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {keys.map((k) => (
                  <tr key={k.id} style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                    <td style={{ padding: "12px 8px" }}>{k.name}</td>
                    <td style={{ padding: "12px 8px", fontFamily: "monospace" }}>
                      {k.key.slice(0, 8)}...{k.key.slice(-4)}
                    </td>
                    <td style={{ padding: "12px 8px" }}>
                      {(k.permissions || []).join(", ") || "—"}
                    </td>
                    <td style={{ padding: "12px 8px" }}>
                      {new Date(k.created_at).toLocaleDateString()}
                    </td>
                    <td style={{ padding: "12px 8px" }}>
                      {k.expires_at ? new Date(k.expires_at).toLocaleDateString() : "Never"}
                    </td>
                    <td style={{ padding: "12px 8px", textAlign: "center" }}>
                      <span
                        style={{
                          padding: "4px 12px",
                          borderRadius: 999,
                          background: k.active ? "rgba(34,197,94,0.2)" : "rgba(239,68,68,0.2)",
                          color: k.active ? "#22c55e" : "#ef4444",
                          fontSize: 13,
                          fontWeight: 600,
                        }}
                      >
                        {k.active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td style={{ padding: "12px 8px", textAlign: "center" }}>
                      <button
                        className="btn-outline"
                        style={{ marginRight: 8, color: k.active ? "#ef4444" : "#22c55e" }}
                        onClick={() => toggleActive(k.id, k.active)}
                      >
                        {k.active ? "Deactivate" : "Activate"}
                      </button>
                      <button
                        className="btn-danger"
                        onClick={() => revokeKey(k.id)}
                      >
                        Revoke
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}